import { LoginResponse } from '@/actions/auth/sign-in'
import { ContactSearchFormData } from '@/app/contacts/search/query-preview-builder'
import { FinalistSearchFormDataDto } from '@/app/entries/finalists/types'
import { EntriesSearchFormData } from '@/app/entries/search/types'
import { ReportSummaryFormDataDto } from '@/app/reports/summary-reports/types'
import { AdminCurrentUserDto, LoginResponseDto } from '@/types/auth'
import { YearFormDataDto, YearLandingPageFormDataDto } from '@/types/awards'
import { FormElementDto, PaginatedResult } from '@/types/baseTypes'
import { CompanyFormDto, CompanyProfileDto } from '@/types/companies'
import { ContactDto, ContactFormDataDto, ContactProfileDto, ContactSearchDto } from '@/types/contacts'
import { EmployeeDto, EmployeeFormDataDto } from '@/types/employees'
import { EnquiryProfileDto, EnquirySearchFormData } from '@/types/enquiries'
import { JudgesSearchFormData } from '@/types/judges'
import { NominationProfileDto, NominationSearchFormData } from '@/types/nominations'
import ApiResponseError, { ApiError } from '@/utils/ApiResponseError'
import axios, { AxiosError } from 'axios'

const baseURL = '/api'

console.log(`Configuring baseURL: ${baseURL}`)

const apiFetch = axios.create({
	baseURL,
	withCredentials: true,
	transformResponse: [
		data => {
			try {
				return JSON.parse(data)
			} catch (error) {
				return {}
			}
		},
	],
})

apiFetch.interceptors.request.use(
	config => {
		config.headers['Content-Type'] = 'application/json'
		config.headers['Cache-Control'] = 'no-cache'
		return config
	},
	error => {
		return Promise.reject(error)
	},
)

apiFetch.interceptors.response.use(
	response => {
		return response.data
	},
	async (error: Error | AxiosError<ApiError>) => {
		if (axios.isAxiosError(error)) {
			if (error.response?.status === 401) {
				window.location.href = '/auth/login'
				throw Error('Invalid token, redirect to login')
			} else {
				if (error.response?.data !== undefined) {
					throw new ApiResponseError(error.response?.data as ApiError)
				}
				throw new ApiResponseError({
					message: 'unknown error occurred',
					title: 'Oops!',
					redirect: false,
					fieldErrors: [],
				})
			}
		}
		throw error
	},
)

// Employees Auth endpoints
export async function login(email: string, password: string): Promise<LoginResponse> {
	return apiFetch(`/employees/login`, {
		method: 'POST',
		data: { email, password },
	})
}

export async function loginOTP(
	credentials: Partial<Record<'otpCode' | 'email' | 'password', unknown>>,
): Promise<LoginResponseDto> {
	return apiFetch(`/employees/otplogin`, {
		method: 'POST',
		data: { ...credentials },
	})
}

export async function currentUser(): Promise<AdminCurrentUserDto> {
	return apiFetch(`/employees/currentuser`, {
		method: 'GET',
	})
}

export async function checkSession(): Promise<void> {
	return apiFetch(`/employees/session`, {
		method: 'GET',
	})
}

export async function logout(): Promise<void> {
	return apiFetch(`/employees/logout`, {
		method: 'POST',
	})
}

export async function deleteSession(): Promise<void> {
	return apiFetch(`/employees/session`, {
		method: 'GET',
	})
}

export async function createLogin(email: string, password: string, token: string): Promise<LoginResponse> {
	return apiFetch(`/employees/createlogin`, {
		method: 'PUT',
		data: { email, password, token },
	})
}

export async function changeExpiredPassword(
	currentPassword: string,
	newPassword: string,
	newPasswordConfirmation: string,
	employeeId: string,
): Promise<void> {
	return apiFetch(`/employees/changeexpiredpassword`, {
		method: 'PUT',
		data: { currentPassword, newPassword, newPasswordConfirmation, employeeId },
	})
}

export async function forgotPassword(email: string): Promise<void> {
	return apiFetch(`/employees/forgotpassword`, {
		method: 'POST',
		data: { email },
	})
}

export async function resetPassword(password: string, token: string): Promise<void> {
	return apiFetch(`/employees/resetpassword`, {
		method: 'POST',
		data: { password, token },
	})
}

// Contacts endpoints
export async function contactSearchForm(): Promise<ContactSearchFormData> {
	return apiFetch(`/contacts/search/form`, {
		method: 'GET',
	})
}

export async function contactSearch(payload: any): Promise<PaginatedResult<ContactSearchDto>> {
	return apiFetch(`/contacts/search`, {
		method: 'POST',
		data: payload,
	})
}

export async function contactForm(): Promise<ContactFormDataDto> {
	return apiFetch(`/contacts/form`, {
		method: 'GET',
	})
}

export async function getContactProfile(contactId: string): Promise<ContactProfileDto> {
	return apiFetch(`/contacts/${contactId}/profile`, {
		method: 'GET',
	})
}

export async function getContact(contactId: string): Promise<ContactDto> {
	return apiFetch(`/contacts/${contactId}`, {
		method: 'GET',
	})
}

export async function companyForm(): Promise<CompanyFormDto> {
	return apiFetch(`/companies/form`, {
		method: 'GET',
	})
}

export async function getCompanyProfile(companyId: string): Promise<CompanyProfileDto> {
	return apiFetch(`/companies/${companyId}/profile`, {
		method: 'GET',
	})
}

// Nomination endpoints

export async function nominationSearchForm(): Promise<NominationSearchFormData> {
	return apiFetch(`/nominations/search/form`, {
		method: 'GET',
	})
}

export async function getNominationProfile(nominationId: string): Promise<NominationProfileDto> {
	return apiFetch(`/nominations/${nominationId}`, {
		method: 'GET',
		// todo: @apuckey this doesn't work with axios
		//next: { tags: ["nominationProfile"] }
	})
}

// Employees Access
export async function getEmployees(): Promise<Array<EmployeeDto>> {
	return apiFetch(`/employees`, {
		method: 'GET',
	})
}

export async function getEmployeeFormData(): Promise<EmployeeFormDataDto> {
	return apiFetch(`/employees/form`, {
		method: 'GET',
	})
}

//Awards Page
export async function getAwardsSearchFormData(): Promise<YearLandingPageFormDataDto> {
	return apiFetch(`/years/landing/form`, {
		method: 'GET',
	})
}

export async function getAwardFormData(): Promise<YearFormDataDto> {
	return apiFetch(`/years/form`, {
		method: 'GET',
	})
}

// Entries
export async function entriesSearchForm(): Promise<EntriesSearchFormData> {
	return apiFetch(`/entries/search/form`, {
		method: 'GET',
	})
}

export async function getMultiassetFormData(): Promise<FinalistSearchFormDataDto> {
	return apiFetch(`/entries/multiasset/form`, {
		method: 'GET',
	})
}

export async function getFinalistFormData(): Promise<FinalistSearchFormDataDto> {
	return apiFetch(`/entries/finalists/form`, {
		method: 'GET',
	})
}

export async function getRevealResultsFormData(): Promise<FinalistSearchFormDataDto> {
	return apiFetch(`/entries/revealresults/form`, {
		method: 'GET',
	})
}

// Enquiries Endpoints
export async function enquirySearchForm(): Promise<EnquirySearchFormData> {
	return apiFetch(`/enquiries/search/form`, {
		method: 'GET',
	})
}

export async function getEnquiryProfile(enquiryId: string): Promise<EnquiryProfileDto> {
	return apiFetch(`/enquiries/${enquiryId}`, {
		method: 'GET',
	})
}

export async function getEnquiryStatues(): Promise<FormElementDto[]> {
	return apiFetch(`/enquiries/statuses`, {
		method: 'GET',
	})
}

// Judging Endpoints
export async function judgesSearchForm(): Promise<JudgesSearchFormData> {
	return apiFetch(`/judges/conflicts/form`, {
		method: 'get',
	})
}

// Reports Endpoints
export async function reportsSummaryForm(): Promise<ReportSummaryFormDataDto> {
	return apiFetch(`/reports/summary/form`, {
		method: 'GET',
	})
}
